import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import Checkbox from '@ui-elements/Checkbox';
import StatusArea from '@ui-parts/StatusArea';
import TextFieldWithLabel from '@ui-parts/TextFieldWithLabel';
import SaveButton from '@ui-elements/SaveButton';
import BasicModal from '@ui-parts/BasicModal';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FormLabel, FormControl, Typography } from '@mui/material';
import RequiredLabel from '@ui-elements/RequiredLabel';
import Box from '@mui/material/Box';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import InputValidationErrorTooltip from '@ui-parts/InputValidationErrorTooltip';
import { FONT_SIZES, PALETTE, Z_INDEX } from '~/assets/styles/base';
import { SURVEY_STATUSES, REJECT_REASON_CATEGORIES, REJECT_REASON_MAX_LENGTH } from '~/constants';
import { callThunk, fetchSavedDeliverySettings, changeSurveyStatus } from '~/api';

const Flex = styled.div`
  display: flex;
`;

const FlexEven = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const BorderBox = styled.div`
  padding: 10px;
  border: solid ${PALETTE.border.frame} 1px;
  background: ${PALETTE.bg.main};
  width: 100%;
`;

const StyledFormLabel = styled(FormLabel)`
  font-size: ${FONT_SIZES.bodyDefault};
  ${({ $css }) => $css};
`;

const StyledAlert = styled.div`
  border: 1px solid #e33f3f;
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 12px 16px;
  margin-top: 15px;
  position: relative;
  width: 100%;
`;

const StyledAlertTypography = styled(Typography)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  /* word-break: keep-all; */
  svg {
    font-size: 18px;
    margin-right: 2px;
  }
`;
function SurveyStatusModal({ open, handleClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const survey = useSelector((state) => state.survey);
  const validation = useSelector((state) => state.validation.changeSurveyStatus);

  const initialRejectReasons = {};
  // eslint-disable-next-line no-return-assign
  REJECT_REASON_CATEGORIES.forEach((reason) => (initialRejectReasons[reason.id] = false));

  const [rejectReason, setRejectReason] = useState('');
  const [checkedRejectCategories, setCheckedRejectReasons] = useState(initialRejectReasons);

  // ステータスラジオボタンの初期値をstoreのsurvey.surveyStatusIdの値によって決定する。
  // 承認/却下じゃなければ、却下を初期値とする（モーダル閉じた時ように初期値を別で保持しておく）。
  const [selectedStatusId, setSelectedStatusId] = useState(null);
  useEffect(() => {
    if ([SURVEY_STATUSES.APPROVED, SURVEY_STATUSES.REJECTED].includes(survey.surveyStatusId)) {
      setSelectedStatusId(survey.surveyStatusId);
    }
  }, [survey.surveyStatusId]);

  /**
   * ローカルステートとvalidationSliceのエラーの初期化
   */
  const clearError = () => {
    dispatch({
      type: 'validation/validationInitialized',
      payload: {
        eventType: 'changeSurveyStatus',
      },
    });
  };

  const handleRadioChange = (e) => {
    clearError();
    setSelectedStatusId(Number(e.target.value));
  };

  const handleChangeRejectReason = (e) => {
    setRejectReason(e.target.value);
  };

  const handleChangeRejectCategoriesCheckbox = (isChecking, id) => {
    setCheckedRejectReasons({
      ...checkedRejectCategories,
      [id]: isChecking,
    });
  };

  const handleClickSave = () => {
    // rejectReasonをトリムする
    const trimmedRejectReason = rejectReason.trim();
    setRejectReason(trimmedRejectReason);

    // バリデーションを発火させる
    dispatch({
      type: 'validation/validateSurveyStatusChanged',
      payload: {
        surveyStatusId: selectedStatusId,
        rejectReason: trimmedRejectReason,
        checkedRejectCategories,
      },
    });
  };

  /**
   * グローバルステートのvalidationのstatusに依拠するhook。
   * validationが全部OKなら、承認／却下APIを呼ぶ
   */
  useEffect(() => {
    (async () => {
      if (validation.status.valid) {
        await dispatch(
          callThunk({
            action: changeSurveyStatus,
            payload: {
              surveyId: survey.id,
              surveyStatusId: selectedStatusId,
              rejectReason,
              checkedRejectCategories,
            },
          })
        );
        await dispatch(
          callThunk({
            action: fetchSavedDeliverySettings,
            payload: { surveyId: survey.id },
          })
        );
        // validationエラーを全て初期化
        dispatch({
          type: 'validation/validationInitialized',
          payload: {
            eventType: 'all',
          },
        });

        handleClose();
      }
    })();
  }, [validation.status.valid]);

  return (
    <BasicModal
      open={open}
      width="1000px"
      titleProps={{
        title: t('surveyStatusModal.changeSurveyStatus'),
      }}
      handleClose={() => {
        // ステータスの選択状態を初期化する
        setSelectedStatusId(null);
        clearError();
        handleClose();
      }}
      buttons={[
        <SaveButton
          key="button1"
          handleClick={() => {
            clearError();
            handleClickSave();
          }}
          data-testid="surveyStatusModal-SaveButton"
          sx={{ width: 150 }}
        />,
      ]}
    >
      <Box>
        <Flex>
          {validation.errors.surveyStatusId.invalid && (
            <StyledAlert severity="error" color="error">
              <StyledAlertTypography variant="body3">
                <WarningRoundedIcon />
                <span data-testid="surveyStatusId-Error-Message">
                  {validation.errors.surveyStatusId.message}
                </span>
              </StyledAlertTypography>
            </StyledAlert>
          )}
        </Flex>
        <StatusArea
          value={selectedStatusId}
          handleRadioChange={handleRadioChange}
          data-testid="StatusArea"
        >
          {/* child-component1 (for rejected radio button) */}
          <Flex>
            <BorderBox>
              <InputValidationErrorTooltip
                error={validation.errors.rejectReason.invalid}
                message={validation.errors.rejectReason.message}
                placement="bottom-end"
                dataTestId="RejectedReason"
                zIndex={Z_INDEX.max}
              >
                <Box>
                  <TextFieldWithLabel
                    required
                    label={t('surveyStatusModal.comment')}
                    name="rejectReason"
                    handleChange={handleChangeRejectReason}
                    dataTestId="surveyStatusModal-RejectReason"
                    value={rejectReason}
                    rows={3}
                    disabled={selectedStatusId !== SURVEY_STATUSES.REJECTED}
                    error={validation.errors.rejectReason.invalid}
                    width="760px"
                    labelColor="secondary"
                    inputProps={{
                      maxLength: REJECT_REASON_MAX_LENGTH,
                    }}
                  />
                </Box>
              </InputValidationErrorTooltip>
              <Box sx={{ padding: 2 }}>
                <FormControl fullWidth error={validation.errors.rejectCategory.invalid}>
                  <StyledFormLabel focused={false} $css="margin-bottom: 15px">
                    <RequiredLabel />
                    <Typography variant="bodyDefault" color="secondary">
                      {t('surveyStatusModal.rejectedReason')}
                    </Typography>
                  </StyledFormLabel>
                  <Flex>
                    {validation.errors.rejectCategory.invalid && (
                      <StyledAlert severity="error" color="error">
                        <StyledAlertTypography variant="body3">
                          <WarningRoundedIcon />
                          <span data-testid="rejectCategory-Error-Message">
                            {validation.errors.rejectCategory.message}
                          </span>
                        </StyledAlertTypography>
                      </StyledAlert>
                    )}
                  </Flex>
                  <FlexEven data-testid="rejectReasonCheckBoxGroup">
                    {REJECT_REASON_CATEGORIES.map((reason) => (
                      <Checkbox
                        key={reason.id}
                        name="rejectCategory"
                        label={reason.category}
                        value={reason.id}
                        checked={checkedRejectCategories[reason.id]}
                        handleChange={(e) => {
                          handleChangeRejectCategoriesCheckbox(e.target.checked, reason.id);
                        }}
                        disabled={selectedStatusId !== SURVEY_STATUSES.REJECTED}
                        dataTestId={`surveyStatusModal-rejectCategory-${reason.id}`}
                        checkboxWidth="195px"
                      />
                    ))}
                  </FlexEven>
                </FormControl>
              </Box>
            </BorderBox>
          </Flex>
        </StatusArea>
      </Box>
    </BasicModal>
  );
}

SurveyStatusModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default SurveyStatusModal;
